import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { createContext, MouseEvent, useMemo, useState } from "react";

import Button from "@/components/Button";
import { PageHeading, TeamHeading } from "@/components/Layout/Parts";
import { OptimisationProjectResource } from "@/types/resources";
import { api } from "@/utils/api";
import OptimisationTestInfoCard from "./components/OptimisationTestInfoCard";
import OptimisationTestVariations from "./components/OptimisationTestVariations";
import OptimisationExport from "./components/OptimisationExport";
import useAlerts from "@/hooks/useAlerts";
import {
  GaNetworkType,
  OptimisationProjectStatusEnum,
  OptimisationTestTypeEnum,
} from "@/utils/enums";
import ThumbsAndTitles from "./components/ThumbsAndTitles";
import Tips from "./components/Tips";
import SequencesAndIntros from "./components/SequencesAndIntros";
import Finalisation from "./components/Finalisation";
import { OptimisationProjectNetworkEnum } from "@/utils/enums";
import ContentSpinner from "@/components/ContentSpinner";
import EditOptimisationProjectForm from "./components/EditOptimisationProjectForm";
import OptimisationProjectNotifications from "./components/OptimisationProjectNotifications";
import useFailedJobsNotifications from "@/hooks/optimisation/useFailedJobsNotifications";
import BackButton from "@/components/BackButton";

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  button {
    flex-shrink: 0;
  }
`;

const RunProjectButton = styled(Button)`
  width: 100%;
  margin-top: 40px;
  &:disabled {
    background: #ededed;
  }
`;

const StyledPageHeading = styled(PageHeading)`
  flex: 1;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 30px;
`;

const EditButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  background: transparent;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

export const GaNetworkTypeContext = createContext<{
  gaNetworkType?: GaNetworkType;
  setGaNetworkType?: React.Dispatch<React.SetStateAction<GaNetworkType>>;
}>({});

const OptimisationProject = () => {
  const { id, teamId } = useParams();

  const [gaNetworkType, setGaNetworkType] = useState(GaNetworkType.WATCH);

  const [showEditForm, setShowEditForm] = useState(false);

  const navigate = useNavigate();

  const alerts = useAlerts();

  const { isLoading, data: project } = useQuery<OptimisationProjectResource>(
    ["optimisation_tests", { id }],
    () =>
      api.get<never, OptimisationProjectResource>(
        `/v1/optimisation-tests/${id}`
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any).response.status === 404) {
          return navigate("/404", {
            replace: true,
          });
        }
        alerts.error();
      },
    }
  );

  const queryClient = useQueryClient();

  const { notifications, queryKey: notificationsQueryKey } =
    useFailedJobsNotifications(project?.id, !!project?.id);

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to delete project?`,
        `${project!.projectId ? project!.projectId + " / " : ""}${
          project!.name
        }`
      );

      if (!confirmed) return;

      await api.delete<never, { id: number }>(`/v1/optimisation-tests/${id}`);

      alerts.success("Project was successfully deleted");

      navigate(`/teams/${teamId}/optimisation-projects`);
    } catch (err) {
      alerts.error();
    }
  };

  const onProjectEnd = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to stop the project?`,
        `${project!.projectId} / ${project!.name}`
      );

      if (!confirmed) return;

      await api.put<
        never,
        { id: number; status: OptimisationProjectStatusEnum }
      >(`/v1/optimisation-tests/${project!.id}/status`, {
        status: OptimisationProjectStatusEnum.ENDED,
      });

      alerts.success("Project has been successfully stopped");

      queryClient.invalidateQueries(["optimisation_tests"], {
        exact: false,
      });
    } catch (err) {
      alerts.error();
    }
  };

  const onProjectStart = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to start the project?`,
        `${project!.projectId} / ${project!.name}`
      );

      if (!confirmed) return;

      await api.put<
        never,
        { id: number; status: OptimisationProjectStatusEnum }
      >(`/v1/optimisation-tests/${project!.id}/status`, {
        status: OptimisationProjectStatusEnum.RUNNING,
      });

      alerts.success("Project has been successfully started");

      queryClient.invalidateQueries(["optimisation_tests"], {
        exact: false,
      });

      document.getElementById("dashboard-scrollable-container")?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (err) {
      alerts.error();
    }
  };

  const renderActionButtons = () => {
    switch (project?.status) {
      case OptimisationProjectStatusEnum.DRAFT:
        return (
          <>
            <EditButton color="secondary" onClick={() => setShowEditForm(true)}>
              Edit
            </EditButton>
            <Button onClick={onProjectStart}>Run project</Button>
          </>
        );
      case OptimisationProjectStatusEnum.RUNNING:
        return (
          <>
            <EditButton color="secondary" onClick={() => setShowEditForm(true)}>
              Edit
            </EditButton>
            <Button onClick={onProjectEnd}>Stop project</Button>
          </>
        );
      default:
        return (
          <>
            <EditButton color="secondary" onClick={onDelete}>
              Delete
            </EditButton>
            <Button onClick={onProjectStart}>Start over</Button>
          </>
        );
    }
  };

  const failedTestsIds = useMemo(
    () => notifications?.map((n) => n.data.meta.testUnitId) ?? [],
    [notifications]
  );
  const units = useMemo(
    () =>
      project?.units?.map((unit) => ({
        ...unit,
        hasError: failedTestsIds.includes(unit.id),
      })),
    [project, failedTestsIds]
  );

  const thumbTests = useMemo(
    () =>
      units?.filter((test) => test.type === OptimisationTestTypeEnum.THUMBS),
    [units]
  );

  const tipTests = useMemo(
    () => units?.filter((test) => test.type === OptimisationTestTypeEnum.TIPS),
    [units]
  );

  const sequenceTests = useMemo(
    () =>
      units?.filter((test) => test.type === OptimisationTestTypeEnum.SEQUENCES),
    [units]
  );

  const introTests = useMemo(
    () => units?.filter((test) => test.type === OptimisationTestTypeEnum.INTRO),
    [units]
  );

  const finalisationTests = useMemo(
    () =>
      units?.filter(
        (test) => test.type === OptimisationTestTypeEnum.FINALISATION
      ),
    [units]
  );

  const titleTests = useMemo(
    () => units?.filter((test) => test.type === OptimisationTestTypeEnum.TITLE),
    [units]
  );

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  return (
    <>
      <AnimatePresence>
        {showEditForm && (
          <EditOptimisationProjectForm
            optimisationProject={project}
            onClose={() => setShowEditForm(false)}
          />
        )}
      </AnimatePresence>
      <TeamHeading />
      <HeadingRow>
        <StyledPageHeading>{project.name}</StyledPageHeading>
        <ActionButtons>{renderActionButtons()}</ActionButtons>
      </HeadingRow>
      <BackButton link={`/teams/${teamId}/optimisation-projects`} />
      <OptimisationProjectNotifications
        notifications={notifications}
        queryKey={notificationsQueryKey}
      />
      <OptimisationTestInfoCard project={project} />
      <OptimisationTestVariations testsCounts={project.testsCounts} />
      <GaNetworkTypeContext.Provider
        value={{ gaNetworkType, setGaNetworkType }}
      >
        <section id="thumbs-section">
          <ThumbsAndTitles
            network={project.network}
            externalProjectId={project.projectId}
            tests={thumbTests}
            projectTitle={project.name}
            competition={project?.thumbsCompetition}
          />
        </section>
        <section id="tips-section">
          <Tips
            network={project.network}
            externalProjectId={project.projectId}
            tests={tipTests}
            projectTitle={project.name}
          />
        </section>
        <section id="sequences-section">
          <SequencesAndIntros
            network={project.network}
            externalProjectId={project.projectId}
            type={OptimisationTestTypeEnum.SEQUENCES}
            tests={sequenceTests}
            projectTitle={project.name}
          />
        </section>
        <section id="intros-section">
          <SequencesAndIntros
            network={project.network}
            externalProjectId={project.projectId}
            type={OptimisationTestTypeEnum.INTRO}
            tests={introTests}
            projectTitle={project.name}
          />
        </section>
        <section id="finalisation-section">
          <Finalisation
            network={project.network}
            externalProjectId={project.projectId}
            tests={finalisationTests}
            projectTitle={project.name}
          />
        </section>
        <section id="titles-section">
          <Finalisation
            isForTitles
            network={project.network}
            externalProjectId={project.projectId}
            tests={titleTests}
            projectTitle={project.name}
          />
        </section>
      </GaNetworkTypeContext.Provider>
      {project.network === OptimisationProjectNetworkEnum.YOUTUBE && (
        <OptimisationExport name={project.name} />
      )}
      {project.status === OptimisationProjectStatusEnum.DRAFT && (
        <RunProjectButton
          onClick={onProjectStart}
          disabled={!!!project.units!.length}
        >
          Run project
        </RunProjectButton>
      )}
    </>
  );
};

export default OptimisationProject;
